import {
  Button,
  Flex,
  FilterType,
  Table,
  TableFilter,
  RoleGuard,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import countryList from 'react-select-country-list';
import {
  faFilter,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Grid } from '@material-ui/core';

import {
  getBrands,
  getBrandIdentityStatus,
  getAuthenticationPlusStatus,
  downloadBrandsAsCsv,
} from '../../apis';
import { BrandListingRow } from '../../components';
import { MAX_RECORD_LIMIT } from '../../constants';
import { BrandDetail } from '../../types';
import { fetchBrandSuggestions, getMnoList } from '../../../Campaigns/apis';
import DownloadLimitedModal from '../../../Campaigns/components/DownloadLimitedModal';
import { BrandFilterErrorCode as FilterErrorCode } from '../../../index';
import { PageTitle } from '@/components';
import { S3_ASSETS_PATH, ENTITY_TYPES } from '@/constants';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import Loader from '@/shared_elements/containers/Loader';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { Mno } from '@/utils/types';
import { fieldValidation } from '@/utils/validator';

import '@/assets/styles/brand-listing-module.scss';

const fetchBrandNameSuggestions = async (query = {}) => {
  const suggestions = await fetchBrandSuggestions(query);
  return suggestions.map((suggestion) => suggestion.brandName);
};

const fetchBrandCompanyNameSuggestions = async (query = {}) => {
  const suggestions = await fetchBrandSuggestions(query);
  return suggestions.map((suggestion) => suggestion.brandCompanyName);
};

const countryOptions = countryList()
  .getData()
  .filter(
    (country: { value: string; label: string }) =>
      !['US', 'CU', 'IR', 'KP', 'SY'].includes(country.value)
  );
countryOptions.unshift({ value: 'US', label: 'United States' });

const FilterConfigs = {
  companyName: {
    type: FilterType.Text,
    label: 'Brand Legal Name',
    placeholder: 'Enter brand legal name',
    suggestions: [],
    width: 260,
  },
  displayName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter brand DBA name',
    suggestions: [],
    width: 260,
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter brand id',
    width: 150,
  },
  entityType: {
    type: FilterType.Dropdown,
    label: 'Entity Type',
    placeholder: 'Select entity type',
    options: ENTITY_TYPES.map((type) => ({
      label: type.value,
      value: type.value,
    })),
    width: 230,
  },
  country: {
    type: FilterType.Dropdown,
    label: 'Country',
    placeholder: 'Select country',
    options: countryOptions,
    width: 230,
  },
  ein: {
    type: FilterType.Text,
    label: 'EIN/Tax number',
    placeholder: 'Enter EIN/Tax number',
    width: 150,
  },
  universalEin: {
    type: FilterType.Text,
    label: 'Universal EIN',
    placeholder: 'Enter Universal EIN',
    width: 150,
  },
  authenticationPlusStatus: {
    type: FilterType.Dropdown,
    label: 'Auth+ Compliant',
    placeholder: 'Select auth+ compliant',
    options: [],
    width: 230,
  },
  identityStatus: {
    type: FilterType.Dropdown,
    label: 'Identity Status',
    placeholder: 'Select identity status',
    options: [],
    width: 230,
  },
  status: {
    type: FilterType.Dropdown,
    label: 'TCR Status',
    placeholder: 'Select TCR status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deleted', value: 'DELETED' },
    ],
    width: 200,
  },
  referenceId: {
    type: FilterType.Text,
    label: 'Reference ID',
    placeholder: 'Enter reference id',
    width: 150,
    error: false,
    helperText: '',
  },
};

const FilterLoaders = {
  suggestion: {
    displayName: {
      minLength: 2,
      load: (value: string) => fetchBrandNameSuggestions({ prefix: value }),
    },
    companyName: {
      minLength: 2,
      load: (value: string) =>
        fetchBrandCompanyNameSuggestions({ companyNamePrefix: value }),
    },
  },
};

const headRows = [
  { id: 'companyName', label: 'BRAND LEGAL NAME', sortable: false },
  { id: 'displayName', label: 'BRAND DBA NAME', sortable: true },
  { id: 'uid', label: 'BRAND ID', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: false },
  { id: 'country', label: 'COUNTRY', sortable: true },
  {
    id: 'authenticationPlusStatus',
    label: 'AUTH+ COMPLIANT',
    sortable: false,
  },
  { id: 'identityStatus', label: 'IDENTITY STATUS', sortable: false },
  { id: 'status', label: 'TCR STATUS', sortable: false },
  { id: 'brandSuspended', label: 'BRAND SUSPENDED', sortable: false },
];

export interface ErrorCodeType {
  [name: string]: {
    [key: number | string]: string | boolean | number;
  };
}

const BrandList: React.FC = () => {
  const {
    configs: filterConfigs,
    candidateFilters,
    appliedFilters,
    additionalFilters,
    handleEdit: handleFiltersEdit,
    handleApply: handleFiltersApply,
    setOptions: setFilterOptions,
    updateConfigObject: updateFilterConfigs,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const [filtersShown, setFiltersShown] = useState(false);
  const [mnos, setMnos] = useState<Mno[]>([]);
  const [downloadLimitedModal, setDownloadLimitedModal] = useState(false);
  const [authenticationPlusStatus, setAuthenticationPlusStatus] = useState<
    AuthenticationPlusStatus[]
  >([]);

  const fetchBrands = async () => {
    const queries = getQueries() as Record<string, any>;
    const response = await getBrands(queries);
    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalBrands,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: brands,
    pagination,
    filter: tableFilters,
  } = useTable<BrandDetail>(fetchBrands, {
    cappingTotal: MAX_RECORD_LIMIT,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  const noBrandsFound = useMemo(
    () =>
      !pageLoading &&
      !tableLoading &&
      totalBrands === 0 &&
      Object.keys(appliedFilters).length === 1 &&
      appliedFilters.status === 'ACTIVE',
    [totalBrands, appliedFilters]
  );

  useEffect(() => {
    document.title = 'The Campaign Registry - Brands';
    fetchFilterOptions();
    initialize();
  }, []);

  const getQueries = () => {
    return {
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
    } as Record<string, any>;
  };

  const setLoadingMessage = (message: string) => {
    dispatch({ type: SET_LOADING_MESSAGE, payload: message });
  };
  const clearLoadingMessage = () => {
    dispatch({ type: CLEAR_LOADING_MESSAGE });
  };

  const fetchFilterOptions = async () => {
    const identityStatus = await getBrandIdentityStatus();
    setFilterOptions(
      'identityStatus',
      identityStatus.map((status) => ({ label: status, value: status }))
    );
    const authenticationPlusStatus = await getAuthenticationPlusStatus();
    setAuthenticationPlusStatus(authenticationPlusStatus);
    setFilterOptions(
      'authenticationPlusStatus',
      authenticationPlusStatus.map(
        (type) => ({ label: type.displayName, value: type.id } as SelectItem)
      )
    );
  };

  const initialize = async () => {
    const mnoList = await getMnoList();
    mnoList.sort((a, b) => {
      if (a.osrBitmaskIndex > b.osrBitmaskIndex) {
        return 1;
      } else if (a.osrBitmaskIndex < b.osrBitmaskIndex) {
        return -1;
      }
      return 0;
    });
    setMnos(mnoList);
  };

  const resetFilterConfigsError = (newValues: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(newValues).forEach((key) => {
      if (
        tempFilterConfigs[key]?.error &&
        newValues[key] !== candidateFilters[key]
      ) {
        tempFilterConfigs[key].error = false;
        tempFilterConfigs[key].helperText = '';
      }
    });
    updateFilterConfigs(tempFilterConfigs);
  };

  const validateFilters = (values: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(values).forEach((key) => {
      if (key in FilterErrorCode) {
        const code = fieldValidation({
          ...(FilterErrorCode as ErrorCodeType)[`${key}Obj`],
          fieldval: values[key],
        });
        if (code !== 0) {
          tempFilterConfigs[key].error = true;
          tempFilterConfigs[key].helperText = String(
            (FilterErrorCode as ErrorCodeType)[key][code]
          );
        }
      }
    });
    updateFilterConfigs(tempFilterConfigs);
    return Object.keys(values).every((key) => !tempFilterConfigs[key].error);
  };

  const handleCandidateFiltersChange = (values: Record<string, string>) => {
    resetFilterConfigsError(values);
    handleFiltersEdit(values);
  };

  const handleAppliedFiltersChange = (values: Record<string, string>) => {
    if (!validateFilters(values)) {
      return;
    }

    if (Object.keys(values).length === 0) {
      resetFilterConfigsError(
        filterConfigs as unknown as Record<string, string>
      );
    }
    handleFiltersApply(values);
  };

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    const queries = getQueries();
    if ('page' in queries) {
      delete queries.page;
    }
    const response = await downloadBrandsAsCsv(queries);
    if (response) {
      const file = new File([response], 'brands.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download brands', 'error');
    }
    clearLoadingMessage();
  };

  const handleBrandAdd = () => {
    history.push('brand/create');
  };

  return (
    <section className="brand-listing-section" style={{ padding: '0px' }}>
      {pageLoading ? (
        <Loader />
      ) : noBrandsFound ? (
        <Container maxWidth={false} style={{ padding: 0 }}>
          <Grid
            container
            className="no-brands-found"
            alignItems="center"
            justifyContent="center"
          >
            <Grid item className="text-center">
              <div className="outer-wrapper">
                <img
                  src={`${S3_ASSETS_PATH}/images/no-campaigns-found.svg`}
                  alt=""
                />
                <h3 className="heading1">Please register a Brand</h3>
                <p className="heading1">No Brands found</p>
              </div>
              <RoleGuard feature="brandList.addBrand">
                <div className="bottom-blk">
                  <Link
                    to="/brand/create"
                    className="primary-btn"
                    data-testid="brandListRegisterBrandLink"
                  >
                    Register a Brand
                  </Link>
                </div>
              </RoleGuard>
            </Grid>
          </Grid>
        </Container>
      ) : (
        <Container maxWidth={false} style={{ padding: '0px' }}>
          <PageTitle
            note={`Results capped at first ${MAX_RECORD_LIMIT.toLocaleString()} records`}
          >
            {totalBrands > 0 ? `${totalBrands} Brands` : 'Brands'}
          </PageTitle>
          <Flex
            sx={{
              mt: 4,
              alignItems: 'center',
              gap: 12,
              mb: 12,
            }}
          >
            <Button
              color="secondary"
              onClick={() => setFiltersShown(!filtersShown)}
              data-testid="tableAddFilterButton"
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
            </Button>
            <Button
              variant="outline"
              color="secondary"
              onClick={() => {
                if (totalBrands > MAX_RECORD_LIMIT) {
                  setDownloadLimitedModal(true);
                } else {
                  handleDownload();
                }
              }}
              data-testid="tableDownloadButton"
            >
              <FontAwesomeIcon icon={faArrowDownToLine} />
              Download
            </Button>
            <Flex sx={{ flex: 1 }} />
            <RoleGuard feature="brandList.addBrand">
              <Button
                variant="outline"
                onClick={handleBrandAdd}
                data-testid="brandListCreateBrandLink"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Brand
              </Button>
            </RoleGuard>
          </Flex>
          {filtersShown && (
            <Grid container style={{ marginBottom: 12 }}>
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={(values: Record<string, string>) =>
                  handleCandidateFiltersChange(values)
                }
                onAppliedValuesChange={(values: Record<string, string>) =>
                  handleAppliedFiltersChange(values)
                }
              />
            </Grid>
          )}
          <Grid container justifyContent="center" spacing={0}>
            <Table
              testId="brandListTable"
              headRows={headRows}
              emptyState="no brands to view"
              className="brands-listing-table"
              loading={tableLoading}
              tableData={brands.map((record) => (
                <BrandListingRow
                  key={record.uid}
                  data={record}
                  mnos={mnos}
                  authenticationPlusStatus={authenticationPlusStatus}
                />
              ))}
              handleChangePage={handlePageChange}
              createSortHandler={handleSortingChange}
              filter={tableFilters}
              pagination={pagination}
            />
          </Grid>
          <DownloadLimitedModal
            open={downloadLimitedModal}
            onClose={() => setDownloadLimitedModal(false)}
            onSubmit={() => {
              setDownloadLimitedModal(false);
              handleDownload();
            }}
          />
        </Container>
      )}
    </section>
  );
};

export default BrandList;
